/**
 * The three numbers above the chart.
 *
 * **Abstand is the one the app never used to compute.** Everything else on this
 * pane restates a measurement; this restates a decision — planned altitude minus
 * the highest thing under it — and it is the number that says whether the plan
 * flies. It carries its severity as a word as well as a colour, because a red
 * number is invisible to a colour-blind pilot in daylight on a tablet.
 *
 * A slot with no value is not rendered and the rest close up (§6's rule, applied
 * here): a route with no planned altitude has no clearance, and an em-dash where
 * a number will be teaches nothing.
 */

import { useT } from '../../i18n';
import { feetText, signedFeet } from './format';
import type { ProfileModel } from './profile';

function Stat({
  label,
  value,
  level,
  note,
}: {
  label: string;
  value: string;
  level?: 'low' | 'none' | undefined;
  note?: string | undefined;
}) {
  return (
    <span className={'aet-stat' + (level ? ' aet-' + level : '')}>
      <span className="aet-stat-label ae-eyebrow">{label}</span>
      <span className="aet-stat-value ae-num">{value}</span>
      {note ? <span className="aet-stat-note">{note}</span> : null}
    </span>
  );
}

export function ClearanceHeader({ model }: { model: ProfileModel }) {
  const t = useT();
  const clearance = model.minClearanceFt;

  return (
    <div className="aet-head">
      {model.highestFt === null ? null : (
        <Stat label={t('terrain.head.highest')} value={feetText(model.highestFt)} />
      )}
      {clearance === null ? (
        <span className="aet-stat">
          <span className="aet-stat-label ae-eyebrow">{t('terrain.head.clearance')}</span>
          <span className="aet-stat-note">{t('terrain.head.noplan')}</span>
        </span>
      ) : (
        <Stat
          label={t('terrain.head.clearance')}
          value={signedFeet(clearance)}
          {...(model.level === 'ok' ? {} : { level: model.level })}
          {...(model.level === 'none'
            ? { note: t('terrain.flag.none') }
            : model.level === 'low'
              ? { note: t('terrain.flag.low') }
              : {})}
        />
      )}
      {model.firstConflict ? (
        <Stat
          label={t('terrain.head.conflict')}
          value={t('terrain.head.conflict.at', {
            n: model.firstConflict.leg,
            nm: model.firstConflict.nm,
          })}
          level="none"
        />
      ) : null}
    </div>
  );
}
